import { getPackagePath, getProductId, RaisePropertyDefinitionMissingError } from '../services';
import { ApiUrl } from '../data/Constants';
import AxiosClient from './AxiosClient';
import { getHeaders } from './HttpUtil';
import { AsyncThunkConfig, GetThunkAPI } from '@reduxjs/toolkit';
import { getPageDetails } from '../store/Helpers/LandingHelper';

import type {
  IAuthorizationRequest,
  IModelRequest,
  IProductCatalogRequest,
  IPropertyAssociationRequest,
  ISubModelRequest,
} from '../../types/IApiTypes';

export const ProductApi = {
  productCatalog: async (payload: IProductCatalogRequest) => {
    const { token, page, limit, searchKey, agCodes, channelCodes } = payload;
    let parmas = { Page: page, Limit: limit };
    if (searchKey) {
      const search = { search: searchKey };
      parmas = { ...search, ...parmas };
    }

    try {
      const response = await AxiosClient.post(
        '/product/v1/catalog',
        {
          agCodes: agCodes ?? [],
          channels: channelCodes ?? [],
        },
        { params: parmas, headers: getHeaders(token)?.headers }
      );
      return {
        productCatalog: response.data || [],
        pageDetails: getPageDetails(response, page, limit),
      };
    } catch {
      return null;
    }
  },

  getProductImages: (action: IModelRequest) => {
    return AxiosClient.get(ApiUrl.ImagesApi, {
      params: { packagePath: getPackagePath(getProductId()), productModel: action.modelId },
      headers: { ...getHeaders(action.token).headers, skipLoader: true },
    })
      .then((response) => {
        return {
          productImages: response.data,
          modelId: action.modelId,
        };
      })
      .catch(() => {
        return {
          modelId: action.modelId,
        };
      });
  },

  productFilter: async (payload: IAuthorizationRequest) => {
    try {
      const response = await AxiosClient.get('/product/v1/filter/read', getHeaders(payload.token));
      return {
        productFilterOptions: response.data,
      };
    } catch {
      return [];
    }
  },

  //Get Property Associations from DB using DIS API
  getPropertyAssociations: async (
    payload: IPropertyAssociationRequest,
    thunkAPI: GetThunkAPI<AsyncThunkConfig>
  ) => {
    const req = {
      packageId: payload.packageId,
      productId: payload.productId,
    };
    try {
      const response = await AxiosClient.post(
        '/product/v1/propertyassociation/read',
        req,
        getHeaders(payload.token)
      );

      //check property definition is available on the response if not show the error dialog
      if (payload.packageId === payload.productId) {
        RaisePropertyDefinitionMissingError(response.data.definitions, thunkAPI);
      }

      return {
        propertiesAssociation: response.data.propertyAssociations,
        propertyDefinition: response.data.definitions ?? payload.propertyDefiniton,
        modelId: payload.productId,
        packageId: payload.packageId,
      };
    } catch {
      //check property definition is available on the response if not show the error dialog
      if (payload.packageId === payload.productId) {
        RaisePropertyDefinitionMissingError([], thunkAPI);
        return null;
      }
    }
  },

  //Get the submodel count variable data
  getSubModelData: async (payload: ISubModelRequest) => {
    const req = {
      productId: payload.productId,
      packageId: payload.packageId,
    };
    try {
      const response = await AxiosClient.post(
        '/product/v1/data/read',
        req,
        getHeaders(payload.token)
      );
      return {
        subModelData: response.data,
        modelId: payload.productId,
      };
    } catch (err) {
      throw new Error(err instanceof Error ? err.message : 'Failed to get submodel data', {
        cause: err,
      });
    }
  },
};
